import { nanoid } from '@reduxjs/toolkit';
import { DEFAULT_STAGE_GRID_HEIGHT, DEFAULT_STAGE_GRID_WIDTH } from '../constants/stageDimensions';
import { normalizeMessageColor } from '../constants/messageBlocks';

const SCRATCH_STAGE_WIDTH = 480;
const SCRATCH_STAGE_HEIGHT = 360;
const DEFAULT_SPRITE_SIZE = { width: 100, height: 100 };
const DEFAULT_BACKDROP_SIZE = { width: SCRATCH_STAGE_WIDTH, height: SCRATCH_STAGE_HEIGHT };

const IMAGE_EXT_BY_MIME = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/svg+xml': 'svg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/bmp': 'bmp',
};

const AUDIO_EXT_BY_MIME = {
    'audio/mpeg': 'mp3',
    'audio/mp3': 'mp3',
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/ogg': 'ogg',
};

const isHexColor = (value) => {
    if (typeof value !== 'string') return false;
    return /^#([0-9a-fA-F]{3}){1,2}$/.test(value.trim());
};

const normalizeHexColor = (value) => {
    if (typeof value !== 'string') return null;
    let hex = value.trim();
    if (!hex.startsWith('#')) {
        hex = `#${hex}`;
    }
    if (!/^#([0-9a-fA-F]{3}){1,2}$/.test(hex)) return null;
    if (hex.length === 4) {
        hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    }
    return hex.toUpperCase();
};

const createColorBackdropSvg = (hex) => {
    const normalized = normalizeHexColor(hex) || '#FFFFFF';
    const svg = [
        '<svg xmlns="http://www.w3.org/2000/svg" width="480" height="360" viewBox="0 0 480 360">',
        `<rect width="100%" height="100%" fill="${normalized}"/>`,
        '</svg>',
    ].join('');
    return new Blob([svg], { type: 'image/svg+xml' });
};

const arrayBufferToBinaryString = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let result = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
        result += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return result;
};

const md5 = (() => {
    const safeAdd = (x, y) => {
        const lsw = (x & 0xffff) + (y & 0xffff);
        const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
        return (msw << 16) | (lsw & 0xffff);
    };

    const bitRotateLeft = (num, cnt) => (num << cnt) | (num >>> (32 - cnt));

    const md5cmn = (q, a, b, x, s, t) => safeAdd(bitRotateLeft(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b);
    const md5ff = (a, b, c, d, x, s, t) => md5cmn((b & c) | (~b & d), a, b, x, s, t);
    const md5gg = (a, b, c, d, x, s, t) => md5cmn((b & d) | (c & ~d), a, b, x, s, t);
    const md5hh = (a, b, c, d, x, s, t) => md5cmn(b ^ c ^ d, a, b, x, s, t);
    const md5ii = (a, b, c, d, x, s, t) => md5cmn(c ^ (b | ~d), a, b, x, s, t);

    const binlMD5 = (x, len) => {
        x[len >> 5] |= 0x80 << (len % 32);
        x[(((len + 64) >>> 9) << 4) + 14] = len;

        let i;
        let a = 1732584193;
        let b = -271733879;
        let c = -1732584194;
        let d = 271733878;

        for (i = 0; i < x.length; i += 16) {
            const oldA = a;
            const oldB = b;
            const oldC = c;
            const oldD = d;

            a = md5ff(a, b, c, d, x[i], 7, -680876936);
            d = md5ff(d, a, b, c, x[i + 1], 12, -389564586);
            c = md5ff(c, d, a, b, x[i + 2], 17, 606105819);
            b = md5ff(b, c, d, a, x[i + 3], 22, -1044525330);
            a = md5ff(a, b, c, d, x[i + 4], 7, -176418897);
            d = md5ff(d, a, b, c, x[i + 5], 12, 1200080426);
            c = md5ff(c, d, a, b, x[i + 6], 17, -1473231341);
            b = md5ff(b, c, d, a, x[i + 7], 22, -45705983);
            a = md5ff(a, b, c, d, x[i + 8], 7, 1770035416);
            d = md5ff(d, a, b, c, x[i + 9], 12, -1958414417);
            c = md5ff(c, d, a, b, x[i + 10], 17, -42063);
            b = md5ff(b, c, d, a, x[i + 11], 22, -1990404162);
            a = md5ff(a, b, c, d, x[i + 12], 7, 1804603682);
            d = md5ff(d, a, b, c, x[i + 13], 12, -40341101);
            c = md5ff(c, d, a, b, x[i + 14], 17, -1502002290);
            b = md5ff(b, c, d, a, x[i + 15], 22, 1236535329);

            a = md5gg(a, b, c, d, x[i + 1], 5, -165796510);
            d = md5gg(d, a, b, c, x[i + 6], 9, -1069501632);
            c = md5gg(c, d, a, b, x[i + 11], 14, 643717713);
            b = md5gg(b, c, d, a, x[i], 20, -373897302);
            a = md5gg(a, b, c, d, x[i + 5], 5, -701558691);
            d = md5gg(d, a, b, c, x[i + 10], 9, 38016083);
            c = md5gg(c, d, a, b, x[i + 15], 14, -660478335);
            b = md5gg(b, c, d, a, x[i + 4], 20, -405537848);
            a = md5gg(a, b, c, d, x[i + 9], 5, 568446438);
            d = md5gg(d, a, b, c, x[i + 14], 9, -1019803690);
            c = md5gg(c, d, a, b, x[i + 3], 14, -187363961);
            b = md5gg(b, c, d, a, x[i + 8], 20, 1163531501);
            a = md5gg(a, b, c, d, x[i + 13], 5, -1444681467);
            d = md5gg(d, a, b, c, x[i + 2], 9, -51403784);
            c = md5gg(c, d, a, b, x[i + 7], 14, 1735328473);
            b = md5gg(b, c, d, a, x[i + 12], 20, -1926607734);

            a = md5hh(a, b, c, d, x[i + 5], 4, -378558);
            d = md5hh(d, a, b, c, x[i + 8], 11, -2022574463);
            c = md5hh(c, d, a, b, x[i + 11], 16, 1839030562);
            b = md5hh(b, c, d, a, x[i + 14], 23, -35309556);
            a = md5hh(a, b, c, d, x[i + 1], 4, -1530992060);
            d = md5hh(d, a, b, c, x[i + 4], 11, 1272893353);
            c = md5hh(c, d, a, b, x[i + 7], 16, -155497632);
            b = md5hh(b, c, d, a, x[i + 10], 23, -1094730640);
            a = md5hh(a, b, c, d, x[i + 13], 4, 681279174);
            d = md5hh(d, a, b, c, x[i], 11, -358537222);
            c = md5hh(c, d, a, b, x[i + 3], 16, -722521979);
            b = md5hh(b, c, d, a, x[i + 6], 23, 76029189);
            a = md5hh(a, b, c, d, x[i + 9], 4, -640364487);
            d = md5hh(d, a, b, c, x[i + 12], 11, -421815835);
            c = md5hh(c, d, a, b, x[i + 15], 16, 530742520);
            b = md5hh(b, c, d, a, x[i + 2], 23, -995338651);

            a = md5ii(a, b, c, d, x[i], 6, -198630844);
            d = md5ii(d, a, b, c, x[i + 7], 10, 1126891415);
            c = md5ii(c, d, a, b, x[i + 14], 15, -1416354905);
            b = md5ii(b, c, d, a, x[i + 5], 21, -57434055);
            a = md5ii(a, b, c, d, x[i + 12], 6, 1700485571);
            d = md5ii(d, a, b, c, x[i + 3], 10, -1894986606);
            c = md5ii(c, d, a, b, x[i + 10], 15, -1051523);
            b = md5ii(b, c, d, a, x[i + 1], 21, -2054922799);
            a = md5ii(a, b, c, d, x[i + 8], 6, 1873313359);
            d = md5ii(d, a, b, c, x[i + 15], 10, -30611744);
            c = md5ii(c, d, a, b, x[i + 6], 15, -1560198380);
            b = md5ii(b, c, d, a, x[i + 13], 21, 1309151649);
            a = md5ii(a, b, c, d, x[i + 4], 6, -145523070);
            d = md5ii(d, a, b, c, x[i + 11], 10, -1120210379);
            c = md5ii(c, d, a, b, x[i + 2], 15, 718787259);
            b = md5ii(b, c, d, a, x[i + 9], 21, -343485551);

            a = safeAdd(a, oldA);
            b = safeAdd(b, oldB);
            c = safeAdd(c, oldC);
            d = safeAdd(d, oldD);
        }
        return [a, b, c, d];
    };

    const rstr2binl = (input) => {
        const output = Array(input.length >> 2);
        for (let i = 0; i < output.length; i += 1) {
            output[i] = 0;
        }
        for (let i = 0; i < input.length * 8; i += 8) {
            output[i >> 5] |= (input.charCodeAt(i / 8) & 0xff) << (i % 32);
        }
        return output;
    };

    const binl2rstr = (input) => {
        let output = '';
        for (let i = 0; i < input.length * 32; i += 8) {
            output += String.fromCharCode((input[i >> 5] >>> (i % 32)) & 0xff);
        }
        return output;
    };

    const rstrMD5 = (s) => binl2rstr(binlMD5(rstr2binl(s), s.length * 8));

    const rstr2hex = (input) => {
        const hexTab = '0123456789abcdef';
        let output = '';
        for (let i = 0; i < input.length; i += 1) {
            const x = input.charCodeAt(i);
            output += hexTab.charAt((x >>> 4) & 0x0f) + hexTab.charAt(x & 0x0f);
        }
        return output;
    };

    return (input) => rstr2hex(rstrMD5(input));
})();

const md5ArrayBuffer = (buffer) => md5(arrayBufferToBinaryString(buffer));

const getExtensionFromUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const clean = url.split('?')[0].split('#')[0];
    const parts = clean.split('.');
    if (parts.length < 2) return null;
    return parts[parts.length - 1].toLowerCase();
};

const getExtensionFromMime = (mime, fallback = null) => {
    if (!mime) return fallback;
    const lowered = mime.toLowerCase();
    return IMAGE_EXT_BY_MIME[lowered] || AUDIO_EXT_BY_MIME[lowered] || fallback;
};

const normalizeDataFormat = (ext) => {
    if (!ext) return null;
    if (ext === 'jpeg') return 'jpg';
    return ext;
};

const getAssetNameFromSource = (source, fallback) => {
    if (typeof source !== 'string') return fallback;
    if (source.startsWith('data:')) return fallback;
    const clean = source.split('?')[0].split('#')[0];
    const parts = clean.split('/');
    const filename = parts[parts.length - 1];
    if (!filename) return fallback;
    const nameParts = filename.split('.');
    nameParts.pop();
    return nameParts.join('.') || fallback;
};

const getImageSize = async (blob, fallback) => {
    if (!blob) return fallback;
    return new Promise((resolve) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
            const width = img.naturalWidth || img.width;
            const height = img.naturalHeight || img.height;
            URL.revokeObjectURL(url);
            if (!width || !height) {
                resolve(fallback);
                return;
            }
            resolve({ width, height });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(fallback);
        };
        img.src = url;
    });
};

const getAudioMeta = async (blob) => {
    if (!blob) return { rate: 44100, sampleCount: 0 };
    const AudioContextRef = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextRef) return { rate: 44100, sampleCount: 0 };
    try {
        const buffer = await blob.arrayBuffer();
        const context = new AudioContextRef();
        const audioBuffer = await context.decodeAudioData(buffer.slice(0));
        const rate = audioBuffer.sampleRate || 44100;
        const sampleCount = audioBuffer.length || 0;
        await context.close();
        return { rate, sampleCount };
    } catch (err) {
        return { rate: 44100, sampleCount: 0 };
    }
};

const toScratchX = (x, gridWidth) => {
    const denom = Math.max(1, gridWidth - 1);
    const ratio = (Number.isFinite(x) ? x : 0) / denom;
    return Math.round(ratio * SCRATCH_STAGE_WIDTH - SCRATCH_STAGE_WIDTH / 2);
};

const toScratchY = (y, gridHeight) => {
    const denom = Math.max(1, gridHeight - 1);
    const ratio = (Number.isFinite(y) ? y : 0) / denom;
    return Math.round(SCRATCH_STAGE_HEIGHT / 2 - ratio * SCRATCH_STAGE_HEIGHT);
};

const isStartTriggerBlock = (block) => block?.category === 'start' && block?.type !== 'sendMessage';

const numInput = (value) => [1, [4, String(value)]];
const textInput = (value) => [1, [10, String(value)]];
const broadcastInput = (id, name) => [1, [11, id, name]];

const getMoveCount = (block) => {
    if (Number.isFinite(block?.count)) return block.count;
    if (Number.isFinite(block?.steps)) return block.steps;
    return 1;
};

const getWaitSeconds = (block) => {
    if (Number.isFinite(block?.duration)) {
        const seconds = block.duration / 1000;
        return Math.max(0.01, Math.round(seconds * 100) / 100);
    }
    if (Number.isFinite(block?.count)) return block.count;
    return 1;
};

const buildScratchBlock = (block, context) => {
    const type = block?.type || block?.name;
    const name = block?.name || block?.type;

    switch (type) {
        case 'Start on Green Flag':
            return { opcode: 'event_whenflagclicked', inputs: {}, fields: {} };
        case 'Start On Tap':
            return { opcode: 'event_whenthisspriteclicked', inputs: {}, fields: {} };
        case 'startOnMessage':
        case 'Start On Message': {
            const color = normalizeMessageColor(block?.messageColor);
            const broadcastName = `message ${color}`;
            const broadcastId = context.getBroadcastId(broadcastName);
            return {
                opcode: 'event_whenbroadcastreceived',
                inputs: {},
                fields: { BROADCAST_OPTION: [broadcastName, broadcastId] },
            };
        }
        case 'sendMessage':
        case 'Send Message': {
            const color = normalizeMessageColor(block?.messageColor);
            const broadcastName = `message ${color}`;
            const broadcastId = context.getBroadcastId(broadcastName);
            return {
                opcode: 'event_broadcast',
                inputs: { BROADCAST_INPUT: broadcastInput(broadcastId, broadcastName) },
                fields: {},
            };
        }
        case 'Move Right':
            return { opcode: 'motion_changexby', inputs: { DX: numInput(getMoveCount(block)) }, fields: {} };
        case 'Move Left':
            return { opcode: 'motion_changexby', inputs: { DX: numInput(-getMoveCount(block)) }, fields: {} };
        case 'Move Up':
            return { opcode: 'motion_changeyby', inputs: { DY: numInput(getMoveCount(block)) }, fields: {} };
        case 'Move Down':
            return { opcode: 'motion_changeyby', inputs: { DY: numInput(-getMoveCount(block)) }, fields: {} };
        case 'go_random_position':
        case 'Go To Random Position':
            return { opcode: 'motion_goto', inputs: {}, fields: { TO: ['_random_', null] } };
        case 'Go Home': {
            const home = context.actor?.homePosition || context.actor?.originalPosition || { x: 0, y: 0 };
            const x = toScratchX(home.x, context.gridWidth);
            const y = toScratchY(home.y, context.gridHeight);
            return { opcode: 'motion_gotoxy', inputs: { X: numInput(x), Y: numInput(y) }, fields: {} };
        }
        case 'Rotate Right': {
            const degrees = getMoveCount(block) * 90;
            return { opcode: 'motion_turnright', inputs: { DEGREES: numInput(degrees) }, fields: {} };
        }
        case 'Rotate Left': {
            const degrees = getMoveCount(block) * 90;
            return { opcode: 'motion_turnleft', inputs: { DEGREES: numInput(degrees) }, fields: {} };
        }
        case 'Say':
        case 'say': {
            const message = block?.message || block?.parameters?.message || block?.value || 'hi';
            return { opcode: 'looks_say', inputs: { MESSAGE: textInput(message) }, fields: {} };
        }
        case 'Grow Size': {
            const delta = getMoveCount(block) * 10;
            return { opcode: 'looks_changesizeby', inputs: { CHANGE: numInput(delta) }, fields: {} };
        }
        case 'Shrink Size': {
            const delta = -getMoveCount(block) * 10;
            return { opcode: 'looks_changesizeby', inputs: { CHANGE: numInput(delta) }, fields: {} };
        }
        case 'Reset Size':
            return { opcode: 'looks_setsizeto', inputs: { SIZE: numInput(100) }, fields: {} };
        case 'Appear':
            return { opcode: 'looks_show', inputs: {}, fields: {} };
        case 'Disappear':
            return { opcode: 'looks_hide', inputs: {}, fields: {} };
        case 'Pop':
            if (!context?.hasPopSound) return null;
            return { opcode: 'sound_play', inputs: {}, fields: { SOUND_MENU: [context.popSoundName, context.popSoundId] } };
        case 'Wait':
            return { opcode: 'control_wait', inputs: { DURATION: numInput(getWaitSeconds(block)) }, fields: {} };
        case 'repeat':
        case 'Repeat':
            return {
                opcode: 'control_repeat',
                inputs: { TIMES: numInput(Math.max(1, getMoveCount(block))), SUBSTACK: [2, null] },
                fields: {},
            };
        case 'repeatBlock':
        case 'Repeat Forever':
            return {
                opcode: 'control_forever',
                inputs: { SUBSTACK: [2, null] },
                fields: {},
            };
        case 'Stop':
            return { opcode: 'control_stop', inputs: {}, fields: { STOP_OPTION: ['this script', null] } };
        default:
            return null;
    }
};

const buildStacks = (scripts) => {
    const blocks = Array.isArray(scripts) ? scripts : [];
    const hasConnections = blocks.some(block => block?.nextBlockId);
    if (hasConnections) {
        const blockMap = new Map(blocks.map(block => [block.id, block]));
        const prevMap = new Map();
        blocks.forEach(block => {
            if (block?.nextBlockId) {
                prevMap.set(block.nextBlockId, block.id);
            }
        });
        const startBlocks = blocks.filter(block => isStartTriggerBlock(block));
        const heads = startBlocks.length ? startBlocks : blocks.filter(block => !prevMap.has(block.id));
        const stacks = [];
        const visited = new Set();
        heads.forEach(head => {
            const chain = [];
            let current = head;
            while (current && !visited.has(current.id)) {
                chain.push(current);
                visited.add(current.id);
                current = current.nextBlockId ? blockMap.get(current.nextBlockId) : null;
            }
            if (chain.length) {
                stacks.push(chain);
            }
        });
        return stacks;
    }

    const ordered = [...blocks].sort((a, b) => {
        const ay = Number.isFinite(a?.y) ? a.y : 0;
        const by = Number.isFinite(b?.y) ? b.y : 0;
        if (ay !== by) return ay - by;
        const ax = Number.isFinite(a?.x) ? a.x : 0;
        const bx = Number.isFinite(b?.x) ? b.x : 0;
        return ax - bx;
    });

    const stacks = [];
    let current = [];
    let sawStart = false;
    ordered.forEach(block => {
        if (isStartTriggerBlock(block)) {
            sawStart = true;
            if (current.length) stacks.push(current);
            current = [block];
            return;
        }
        current.push(block);
    });
    if (current.length) stacks.push(current);
    if (!sawStart) {
        return ordered.length ? [ordered] : [];
    }
    return stacks;
};

export async function buildScratchSb3Project(state, options = {}) {
    const scene = state?.scenes?.[state?.currentSceneIndex] || {};
    const gridWidth = Math.max(1, Math.floor(state?.stageGridWidth || DEFAULT_STAGE_GRID_WIDTH));
    const gridHeight = Math.max(1, Math.floor(state?.stageGridHeight || DEFAULT_STAGE_GRID_HEIGHT));
    const assetCache = new Map();
    const skippedBlocks = [];
    const broadcasts = {};
    const broadcastIds = new Map();

    const getBroadcastId = (name) => {
        if (!broadcastIds.has(name)) {
            const id = nanoid();
            broadcastIds.set(name, id);
            broadcasts[id] = name;
        }
        return broadcastIds.get(name);
    };

    const addAsset = async (blob, ext, nameHint) => {
        const normalizedExt = normalizeDataFormat(ext || 'png');
        const buffer = await blob.arrayBuffer();
        const assetId = md5ArrayBuffer(buffer);
        const md5ext = `${assetId}.${normalizedExt}`;
        if (!assetCache.has(md5ext)) {
            assetCache.set(md5ext, { assetId, md5ext, dataFormat: normalizedExt, blob, nameHint });
        }
        return assetCache.get(md5ext);
    };

    const resolveBlobFromSource = async (source) => {
        try {
            if (source instanceof Blob) return source;
            if (typeof source === 'string') {
                if (source.startsWith('data:')) {
                    const res = await fetch(source);
                    return res.blob();
                }
                const res = await fetch(source);
                if (!res.ok) return null;
                return res.blob();
            }
            return null;
        } catch (err) {
            return null;
        }
    };

    const addImageAsset = async (source, fallbackName, fallbackSize) => {
        let blob;
        let ext = null;
        let name = fallbackName;

        if (typeof source === 'string' && isHexColor(source)) {
            blob = createColorBackdropSvg(source);
            ext = 'svg';
            name = normalizeHexColor(source) || fallbackName;
        } else {
            blob = await resolveBlobFromSource(source);
            if (!blob) return null;
            ext = normalizeDataFormat(getExtensionFromMime(blob.type, getExtensionFromUrl(source)));
            name = getAssetNameFromSource(source, fallbackName);
        }

        const asset = await addAsset(blob, ext, name);
        const size = await getImageSize(blob, fallbackSize);
        return {
            name: name || asset.assetId,
            assetId: asset.assetId,
            md5ext: asset.md5ext,
            dataFormat: asset.dataFormat,
            rotationCenterX: Math.round((size?.width || fallbackSize?.width || 0) / 2),
            rotationCenterY: Math.round((size?.height || fallbackSize?.height || 0) / 2),
        };
    };

    const addSoundAsset = async (source, fallbackName) => {
        const blob = await resolveBlobFromSource(source);
        if (!blob) return null;
        const ext = normalizeDataFormat(getExtensionFromMime(blob.type, getExtensionFromUrl(source)) || 'wav');
        const asset = await addAsset(blob, ext, fallbackName);
        const meta = await getAudioMeta(blob);
        return {
            name: fallbackName || asset.assetId,
            assetId: asset.assetId,
            md5ext: asset.md5ext,
            dataFormat: asset.dataFormat,
            rate: meta.rate,
            sampleCount: meta.sampleCount,
        };
    };

    const backgroundValues = [];
    const sceneBackground = scene?.background;
    if (sceneBackground) backgroundValues.push(sceneBackground);
    if (Array.isArray(state?.backgroundGallery)) {
        state.backgroundGallery.forEach(bg => backgroundValues.push(bg));
    }
    const uniqueBackgrounds = Array.from(new Set(backgroundValues.filter(Boolean)));
    if (!uniqueBackgrounds.length) {
        uniqueBackgrounds.push('#FFFFFF');
    }

    const stageCostumes = [];
    for (const [index, bg] of uniqueBackgrounds.entries()) {
        const costume = await addImageAsset(bg, `Backdrop${index + 1}`, DEFAULT_BACKDROP_SIZE);
        if (costume) stageCostumes.push(costume);
    }

    const currentBackdropIndex = Math.max(
        0,
        uniqueBackgrounds.findIndex(bg => bg === sceneBackground)
    );

    const popSoundSource = state?.sounds?.pop || './assets/sounds/pop.mp3';
    const popSound = await addSoundAsset(popSoundSource, 'pop');
    const popSoundName = popSound?.name || 'pop';
    const popSoundId = popSound?.assetId || nanoid();
    const hasPopSound = !!popSound;

    const stageTarget = {
        isStage: true,
        name: 'Stage',
        variables: {},
        lists: {},
        broadcasts,
        blocks: {},
        comments: {},
        currentCostume: currentBackdropIndex >= 0 ? currentBackdropIndex : 0,
        costumes: stageCostumes,
        sounds: [],
        volume: 100,
        layerOrder: 0,
    };

    const targets = [stageTarget];

    const actorList = Array.isArray(scene?.actors) ? scene.actors : [];
    for (let i = 0; i < actorList.length; i += 1) {
        const actor = actorList[i];
        const costume = await addImageAsset(actor?.image, actor?.name || `Sprite${i + 1}`, DEFAULT_SPRITE_SIZE);
        if (!costume) continue;

        const blocks = {};
        const stacks = buildStacks(actor?.scripts || []);
        const context = {
            getBroadcastId,
            gridWidth,
            gridHeight,
            actor,
            popSoundName,
            popSoundId,
            hasPopSound,
        };

        const convertChain = (chain, parentId = null) => {
            let firstId = null;
            let prevId = null;
            chain.forEach(block => {
                const scratchBlock = buildScratchBlock(block, context);
                if (!scratchBlock) {
                    skippedBlocks.push(block?.type || block?.name || 'unknown');
                    if (Array.isArray(block?.children) && block.children.length) {
                        convertChain(block.children, null);
                    }
                    return;
                }
                const blockId = String(block?.id || nanoid());
                scratchBlock.next = null;
                scratchBlock.parent = prevId || parentId;
                scratchBlock.shadow = false;
                scratchBlock.topLevel = !prevId && !parentId;
                if (scratchBlock.topLevel) {
                    scratchBlock.x = Math.round(Number.isFinite(block?.x) ? block.x : 0);
                    scratchBlock.y = Math.round(Number.isFinite(block?.y) ? block.y : 0);
                }
                blocks[blockId] = scratchBlock;
                if (prevId) {
                    blocks[prevId].next = blockId;
                }
                if (!firstId) firstId = blockId;
                prevId = blockId;

                if (Array.isArray(block?.children) && block.children.length) {
                    const childFirstId = convertChain(block.children, blockId);
                    if (scratchBlock.inputs?.SUBSTACK) {
                        scratchBlock.inputs.SUBSTACK = [2, childFirstId];
                    }
                }
            });
            return firstId;
        };

        stacks.forEach(stack => {
            convertChain(stack, null);
        });

        const spriteTarget = {
            isStage: false,
            name: actor?.name || `Sprite${i + 1}`,
            variables: {},
            lists: {},
            broadcasts: {},
            blocks,
            comments: {},
            currentCostume: 0,
            costumes: [costume],
            sounds: popSound ? [popSound] : [],
            volume: 100,
            layerOrder: i + 1,
            x: toScratchX(actor?.x, gridWidth),
            y: toScratchY(actor?.y, gridHeight),
            direction: Number.isFinite(actor?.direction) ? actor.direction : 90,
            size: Math.round((Number.isFinite(actor?.size) ? actor.size : 1) * 100),
            visible: actor?.visible !== false,
        };
        targets.push(spriteTarget);
    }

    const projectJson = {
        targets,
        monitors: [],
        extensions: [],
        meta: {
            semver: '3.0.0',
            vm: '0.2.0',
            agent: options.agent || 'blockzie-jr-web',
        },
    };

    return {
        projectJson,
        assets: Array.from(assetCache.values()),
        skippedBlocks,
        popSound,
    };
}
